import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { DataStore } from 'aws-amplify';
import { AppUser, UserGroup, UserType } from '../../models';
import { Picker } from '@react-native-picker/picker';
import * as DataStoreUtil from '../../components/DataStoreUtil';
import * as AppConst from '../../components/AppConst';
import * as CmnUtil from '../../components/CmnUtil';
import * as CognitoControl from '../../components/CognitoControl';

/** ================================
*  アカウント操作画面
*  ================================*/
const UpdateUser = ({ closeFunction, userId }: { closeFunction: Function, userId: string }) => {
  // 操作種別
  const controlType = {
    none: 'none',
    enable: 'enable',
    disable: 'disable',
    delete: 'delete',
    transfer: 'transfer',
  }

  // 本人情報
  const [userInfo, setUserInfo] = useState<AppUser | undefined>(undefined);

  // 操作
  const [control, setControl] = useState<string>(controlType.none);

  // 各種情報
  const [userType, setUserType] = useState<string>('');
  const [groupList, setGroupList] = useState<UserGroup[]>([]);
  const [group, setGroup] = useState<string>('');

  useEffect(() => {
    (async () => {
      // ユーザー情報取得
      const appUser = (await DataStore.query(AppUser, userId));
      setUserInfo(appUser);
      if (appUser == undefined) {
        return;
      }

      // 組織変更用の情報
      const groups = (await DataStore.query(UserGroup, c => c.groupType('eq', appUser.type)));
      setUserType(DataStoreUtil.convUserType(appUser.type));
      setGroupList(groups);
      setGroup(appUser.group.id);
    })();
  }, []);

  // 有効化/無効化
  const editUser = async (enabled: boolean) => {
    const appUser = (await DataStore.query(AppUser, userId));
    if (!appUser) {
      CmnUtil.appAlert('既に削除されているユーザーです。');
      CmnUtil.endLoading();
      setControl(controlType.none);
      return;
    }

    // 有効化/無効化
    await DataStore.save(
      AppUser.copyOf(appUser, update => { update.isEnabled = enabled })
    );

    if (enabled) {
      CmnUtil.appMsg('有効化しました。');
    } else {
      CmnUtil.appMsg('無効化しました。');
    }
    CmnUtil.endLoading();
    setControl(controlType.none);
    closeFunction();
  }

  // 削除
  const deleteUser = async () => {
    CmnUtil.startLoading();

    // ユーザーデータ削除
    const result = await delUserData();
    if (!result) {
      CmnUtil.endLoading();
      CmnUtil.appAlert('ユーザー削除に失敗しました。');
      setControl(controlType.none);
      return;
    }

    CmnUtil.endLoading();
    CmnUtil.appMsg('ユーザーを削除しました。');
    setControl(controlType.none);
    closeFunction();
  }

  // 削除
  const delUserData = async () => {
    // cognitoユーザーを削除
    // 画面から削除する手段を残すために、DataStore削除より先に行う
    try {
      await CognitoControl.deleteUser(userInfo!.authUserID);
    } catch (err) {
      console.log(err);
      return false;
    }

    // AppUserを削除
    await DataStore.delete(AppUser, c => c.id('eq', userId));

    return true;
  }

  // 所属組織の変更
  const transferGroup = async () => {
    const appUser = (await DataStore.query(AppUser, userId));
    if (!appUser) {
      CmnUtil.appAlert('既に削除されているユーザーです。');
      CmnUtil.endLoading();
      setControl(controlType.none);
      return;
    }

    const userGroup = (await DataStore.query(UserGroup, group));
    if (!userGroup) {
      CmnUtil.appAlert('既に削除されている組織です。');
      CmnUtil.endLoading();
      setControl(controlType.none);
      return;
    }

    // AppUserの更新
    await DataStore.save(
      AppUser.copyOf(appUser, update => { update.group = userGroup })
    );

    CmnUtil.appMsg('所属組織を変更しました。');
    CmnUtil.endLoading();
    setControl(controlType.none);
    closeFunction();
  }

  return (
    <View style={AppConst.styles.dialog}>
      <View style={AppConst.styles.dialogContents}>
        <View style={{ backgroundColor: AppConst.defaultBaseColor, width: 450, borderRadius: 10 }}>
          <AppConst.DialogHeader closeFunction={closeFunction} />

          <Text style={{ paddingLeft: 10, paddingTop: 10 }}>{userInfo?.username}</Text>

          {/* アカウント操作 */}
          {userInfo && <View style={{ flexDirection: 'row', paddingTop: 10 }}>
            {userInfo.isEnabled && <TouchableOpacity style={[styles.actionButton, styles.dangerousColor]} onPress={() => { setControl(controlType.disable) }}>
              <Text>無効化</Text>
            </TouchableOpacity>}
            {!userInfo.isEnabled && <TouchableOpacity style={[styles.actionButton, styles.positiveColor]} onPress={() => { setControl(controlType.enable) }}>
              <Text>有効化</Text>
            </TouchableOpacity>}
            {!userInfo.isEnabled && <TouchableOpacity style={[styles.actionButton, styles.dangerousColor]} onPress={() => { setControl(controlType.delete) }}>
              <Text>削除</Text>
            </TouchableOpacity>}
          </View>}

          {/* 組織変更 */}
          <View style={{ marginTop: 20, padding: 10, borderTopWidth: 1 }}>
            <Text>組織</Text>
            <View style={{ marginTop: 10 }}>
              <View style={styles.box}>
                <Text>タイプ</Text>
                <Picker
                  selectedValue={userType}
                  style={styles.textInput}
                  enabled={false}
                >
                  <Picker.Item label={DataStoreUtil.convUserType(UserType.INSTRUCTOR)} value={UserType.INSTRUCTOR} />
                  <Picker.Item label={DataStoreUtil.convUserType(UserType.PRODUCER)} value={UserType.PRODUCER} />
                </Picker>
              </View>

              <View style={styles.box}>
                <Text>組織</Text>
                <Picker
                  selectedValue={group}
                  style={styles.textInput}
                  onValueChange={(itemValue, itemIndex) =>
                    setGroup(itemValue)
                  }>
                  {groupList.map((data: UserGroup, index: number) => (
                    <Picker.Item key={index} label={data.groupName} value={data.id} />
                  ))}
                </Picker>
              </View>

              <View style={[styles.box, { alignItems: 'flex-end' }]}>
                <TouchableOpacity style={[styles.actionButton, styles.dangerousColor]} onPress={() => { setControl(controlType.transfer) }}>
                  <Text>組織変更</Text>
                </TouchableOpacity>
              </View>

            </View>
          </View>

          {/* 確認ダイアログ */}
          {control != controlType.none && <View style={[AppConst.styles.dialog, { borderRadius: 10 }]}>
            <View style={AppConst.styles.dialogContents}>
              <View style={{ backgroundColor: AppConst.defaultBaseColor, width: 300, borderRadius: 10 }}>
                <AppConst.DialogHeader closeFunction={() => { setControl(controlType.none) }} />

                {control == controlType.enable && <View style={{ padding: 20 }}>
                  <Text>{userInfo?.username}を有効化しますか？</Text>
                </View>}
                {control == controlType.disable && <View style={{ padding: 20 }}>
                  <Text style={{ color: 'red' }}>危険操作です！！</Text>
                  <Text>無効化すると対象ユーザーはログインできなくなります。</Text>
                  <Text>{userInfo?.username}を無効化しますか？</Text>
                </View>}
                {control == controlType.delete && <View style={{ padding: 20 }}>
                  <Text style={{ color: 'red' }}>危険操作です！！</Text>
                  <Text>削除すると対象ユーザー情報は完全に削除され、復旧できません。</Text>
                  <Text>{userInfo?.username}を削除しますか？</Text>
                </View>}
                {control == controlType.transfer && <View style={{ padding: 20 }}>
                  <Text style={{ color: 'red' }}>危険操作です！！</Text>
                  <Text>所属組織を変更しますか？</Text>
                </View>}

                <View style={{ flexDirection: 'row', height: 40, borderTopWidth: 1, borderColor: '#707070' }}>
                  <TouchableOpacity style={styles.dialogNo} onPress={() => { setControl(controlType.none) }}>
                    <Text>いいえ</Text>
                  </TouchableOpacity>

                  {control == controlType.enable && <TouchableOpacity style={styles.dialogYes} onPress={() => { editUser(true) }}>
                    <Text>はい</Text>
                  </TouchableOpacity>}

                  {control == controlType.disable && <TouchableOpacity style={styles.dialogYes} onPress={() => { editUser(false) }}>
                    <Text>はい</Text>
                  </TouchableOpacity>}

                  {control == controlType.delete && <TouchableOpacity style={styles.dialogYes} onPress={() => { deleteUser() }}>
                    <Text>はい</Text>
                  </TouchableOpacity>}

                  {control == controlType.transfer && <TouchableOpacity style={styles.dialogYes} onPress={() => { transferGroup() }}>
                    <Text>はい</Text>
                  </TouchableOpacity>}
                </View>
              </View>
            </View>
          </View>}

        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  actionButton: {
    width: 80,
    height: 30,
    borderWidth: 1,
    marginLeft: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  userIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
  },
  positiveColor: {
    backgroundColor: '#3cb371',
  },
  dangerousColor: {
    backgroundColor: '#ff6347',
  },
  box: {
    marginTop: 10,
  },
  textInput: {
    borderBottomWidth: 1,
    backgroundColor: AppConst.defaultBaseInputColor,
    height: 30,
    paddingLeft: 5,
  },
  dialogNo: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    borderRightWidth: 1,
    borderColor: '#707070',
  },
  dialogYes: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default UpdateUser;
